import type { FileListItem } from '@sparklayer-io/spark-dash-ui/components/ui/file-upload/types';
import { FileUpload, Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import { ApiResponseError } from '../../../types/errors';
import { useUploadTheme } from '../hooks/use-themes';
import type { Theme, ThemeUploadError } from '../types/theme';

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

function parseUploadErrors(err: unknown): string[] {
  if (
    ApiResponseError.isApiResponseError(err, ApiResponseError.errorSchemas.array) &&
    err.status === 400
  ) {
    return (err.data as ThemeUploadError[]).map((e) =>
      typeof e.line === 'number'
        ? t('features:themes.pages.themes_list_page.modals.upload.line_error', {
            line: e.line,
            field: e.field,
            message: e.message,
          })
        : e.message,
    );
  }
  return [t('features:themes.pages.themes_list_page.modals.upload.generic_error')];
}

export function UploadThemeModal({ theme, onClose }: { theme: Theme | null; onClose: () => void }) {
  const { notificationHandler } = useHandlers();
  const uploadTheme = useUploadTheme();

  const [files, setFiles] = useState<FileListItem[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Reset when the modal is (re)opened for a theme.
  useEffect(() => {
    if (theme) {
      setFiles([]);
      setErrors([]);
    }
  }, [theme]);

  async function handleUpload() {
    if (!theme) {
      return;
    }
    const file = files[0]?.file;
    if (!file) {
      setErrors([t('features:themes.pages.themes_list_page.modals.upload.invalid_zip')]);
      return;
    }

    setErrors([]);
    try {
      const base64 = await toBase64(file);
      await uploadTheme.mutateAsync({ id: theme.id, base64 });
      notificationHandler.success(
        t('features:themes.pages.themes_list_page.notifications.uploaded'),
      );
      onClose();
    } catch (err) {
      setErrors(parseUploadErrors(err));
    }
  }

  return (
    <Modal
      isOpen={theme !== null}
      onClose={onClose}
      primaryAction={{
        text: t('features:themes.pages.themes_list_page.modals.upload.submit'),
        onClick: () => void handleUpload(),
        disabled: files.length === 0 || uploadTheme.isPending,
        loading: uploadTheme.isPending,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: onClose,
      }}
    >
      <Modal.Header>{t('features:themes.pages.themes_list_page.modals.upload.title')}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-3">
          <Text variant="secondary">
            {t('features:themes.pages.themes_list_page.modals.upload.description')}
          </Text>
          <FileUpload
            accept={['.zip', 'application/zip']}
            value={files}
            onChange={setFiles}
            errorTitle={
              errors.length
                ? t('features:themes.pages.themes_list_page.modals.upload.errors_intro')
                : undefined
            }
            error={errors.join('\n')}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}
